import React, {useContext, useEffect, useState} from 'react';
import {UserContext} from '../../utils/UserContext';
import ApplyJobPopup from '../Popups/ApplyJobPopup';
import {useLocation, useNavigate} from 'react-router-dom';
import {hasCreatedProfile, isJobSeeker, userExists} from '../../utils/variables/userVariables';
import {MoreVert, OpenInNew} from '@mui/icons-material';
import {ROUTES} from '../../utils/ROUTES';
import Cookies from 'js-cookie';
import {COOKIES, COOKIE_EXPIRY_TIME} from '../../utils/cookie-names';
import {Button, Checkbox, Dropdown, Form, Input, Modal, notification, Select} from 'antd';
import {deleteJobApi, updateUniqueExternalLinkClicksApi} from '../../api/jobsApi';
import {Alert} from '@mui/material';
import {DeleteOutlined, EditOutlined, EyeOutlined} from '@ant-design/icons';
import {triggerFetchingJobsAfterCloseAtom} from '../../utils/atoms';
import {useAtom} from 'jotai';
import {isBannedCountry} from '../../api/locationApi';
import {SendHorizontal} from 'lucide-react';

export default function ApplyJobButton({job, flexDirection = '', buttonClassName = ''}) {
	const [currentUser] = useContext(UserContext);
	const [applyPopUp, setApplyPopUp] = useState(false);
	const [loginModalVisible, setLoginModalVisible] = useState(false);
	const navigate = useNavigate();
	const location = useLocation();
	const [modalShow, setModalShow] = useState(false);
	const [checked, setChecked] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [, setTriggerFetchingJobsAfterClose] = useAtom(triggerFetchingJobsAfterCloseAtom);
	const [currentJobClosed, setCurrentJobClosed] = useState(false);
	const [form] = Form.useForm();

	// Add this state to track the selected reason
	const [selectedReason, setSelectedReason] = useState('');

	function openLoginModal() {
		setLoginModalVisible(true);
	}

	function closeLoginModal() {
		setLoginModalVisible(false);
	}

	function closeApplyPopup() {
		setApplyPopUp(false);
	}

	function authenticateLoginAction() {
		Cookies.set(COOKIES.current_location, location.pathname, COOKIE_EXPIRY_TIME.THIRTY_MINS);
		Cookies.set(COOKIES.is_single_job_page, 'true', COOKIE_EXPIRY_TIME.THIRTY_MINS);
		Cookies.set(COOKIES.apply_job_redirect, ROUTES.JOB_PAGE.replace(':jobId', job.id), COOKIE_EXPIRY_TIME.THIRTY_MINS);
		navigate(ROUTES.AUTHENTICATE);
	}

	async function applyJob() {
		if (isBannedCountry()) {
			notification.error({
				message: 'Unavailable',
				description: "This Job isn't available in your region",
				duration: 5,
			});
			return;
		}

		if (job?.use_external_link) {
			if (!userExists(currentUser)) {
				openLoginModal();
				return;
			}

			if (!hasCreatedProfile(currentUser)) {
				Cookies.set(COOKIES.apply_job_redirect, ROUTES.JOB_PAGE.replace(':jobId', job.id), COOKIE_EXPIRY_TIME.FIFTEEN_MINS);
				Cookies.set(COOKIES.current_location, location.pathname, COOKIE_EXPIRY_TIME.FIFTEEN_MINS);
				navigate(ROUTES.EDIT_PROFILE);
				return;
			}

			const url = job?.external_link.startsWith('http') ? job?.external_link : `https://${job?.external_link}`;

			// Check if this link has been clicked before using localStorage
			const clickedLinks = JSON.parse(localStorage.getItem('clickedExternalLinks') || '{}');

			if (!clickedLinks[job.id]) {
				try {
					await updateUniqueExternalLinkClicksApi(job.id);
					// Mark this job as clicked
					clickedLinks[job.id] = true;
					try {
						localStorage.setItem('clickedExternalLinks', JSON.stringify(clickedLinks));
					} catch (e) {
						if (e.name === 'QuotaExceededError') {
							localStorage.removeItem('clickedExternalLinks');
							localStorage.setItem(
								'clickedExternalLinks',
								JSON.stringify({
									[job.id]: true,
								})
							);
						}
					}
				} catch (error) {
					console.error('Error updating external link clicks:', error);
				}
			}

			window.open(url, '_blank');
			return;
		}

		if (!userExists(currentUser)) {
			openLoginModal();
			return;
		}

		if (!hasCreatedProfile(currentUser)) {
			Cookies.set(COOKIES.apply_job_redirect, ROUTES.JOB_PAGE.replace(':jobId', job.id), COOKIE_EXPIRY_TIME.FIFTEEN_MINS);
			Cookies.set(COOKIES.current_location, location.pathname, COOKIE_EXPIRY_TIME.FIFTEEN_MINS);
			navigate(ROUTES.EDIT_PROFILE);
			return;
		}
		if (job?.applicants.includes(currentUser.id)) {
			openLoginModal();
			return;
		}

		if (job?.completed) {
			notification.error({
				message: 'Job closed',
				description: 'This position has been filled or is no longer accepting applications. Please explore our other available opportunities.',
				duration: 5,
			});
			return;
		}

		setApplyPopUp(true);
	}

	function handleModalClose() {
		setModalShow(false);
	}

	const deleteJob = async (id) => {
		handleModalClose();
		setIsDeleting(true);
		const res = await deleteJobApi(id, form.getFieldsValue());
		if (res === true) {
			notification.success({
				message: 'Job closed',
				description: 'The job has been closed successfully.',
				duration: 5,
			});
			setTriggerFetchingJobsAfterClose(true);
			setCurrentJobClosed(true);
			setChecked(false);
		} else {
			setIsDeleting(false);
			notification.error({
				message: 'Error',
				description: 'Failed to close job. Please try again.',
				duration: 5,
			});
		}
	};

	function authenticatedButtons() {
		if (currentUser.id === job?.created_by) {
			return (
				<Dropdown
					menu={{
						items: [
							{
								key: '1',
								label: (
									<div className="flex items-center gap-2">
										<EditOutlined /> Edit {job?.completed ? ' (Closed)' : ''} {job?.is_draft ? ' (Draft)' : ''}
									</div>
								),
								onClick: () => navigate(`/edit-job/${job.id}`),
								disabled: job?.completed || currentJobClosed || job?.is_draft,
							},
							{
								key: '2',
								label: (
									<div className="flex items-center gap-2">
										<EyeOutlined /> View all applications
									</div>
								),
								onClick: () => navigate(`/applicants/${job.id}`),
							},
							{
								key: '3',
								label: (
									<div className="flex items-center gap-2">
										<DeleteOutlined /> Close Job {job?.completed ? ' (Closed)' : ''}
									</div>
								),
								danger: true,
								onClick: () => setModalShow(true),
								disabled: job?.completed || currentJobClosed,
							},
						],
					}}>
					<Button icon={<MoreVert />} type="text" />
				</Dropdown>
			);
		} else if (isJobSeeker(currentUser)) {
			const hasApplied = job?.applicants.includes(currentUser.id);
			if (!hasApplied) {
				return (
					<>
						{job?.is_indeed_job !== true ? (
							<Button
								type="primary"
								onClick={applyJob}
								icon={job?.use_external_link ? <OpenInNew className="text-base" /> : <SendHorizontal size={16} strokeWidth={1.5} />}
								disabled={job?.completed}
								className={buttonClassName}>
								Apply {job?.completed ? '(Closed)' : ''}
							</Button>
						) : (
							<a href={job?.indeed_job_url} target="_blank" className="linkButtonLinkBlue min-h-fit flex items-center gap-1 w-fit" rel="noreferrer">
								Apply <OpenInNew style={{fontSize: '1.2em'}} />
							</a>
						)}
					</>
				);
			} else {
				return (
					<Button disabled className={buttonClassName}>
						Applied
					</Button>
				);
			}
		}
		if (!currentUser?.profile) {
			return (
				<Button type="primary" onClick={applyJob} icon={job?.use_external_link ? <OpenInNew className="text-base" /> : null} disabled={job?.completed} className={buttonClassName}>
					Apply {job?.completed ? '(Closed)' : ''}
				</Button>
			);
		}
	}

	return (
		<>
			<ApplyJobPopup
				user={currentUser}
				job={job}
				showPopup={applyPopUp}
				closePopup={closeApplyPopup}
				title={'Apply for this job'}
				textArea={true}
				buttonText={'Apply'}
				disabledButtonText={'Sent'}
			/>
			<Modal
				title="Login required"
				open={loginModalVisible}
				onCancel={closeLoginModal}
				maskClosable={false}
				centered
				maskStyle={{backdropFilter: 'blur(6px)'}}
				footer={[
					<Button key="login" type="primary" onClick={authenticateLoginAction}>
						Login to Continue
					</Button>,
				]}>
				<div className="py-2">
					<p className="text-base mb-2">You need to login to your account before applying for this job.</p>
					<p className="text-sm text-gray-500">You'll be redirected to this job page after login.</p>
				</div>
			</Modal>
			<Modal
				title="Close job"
				open={modalShow}
				onCancel={handleModalClose}
				footer={[
					<Button key="cancel" onClick={handleModalClose}>
						No
					</Button>,
					<Button key="submit" onClick={() => deleteJob(job.id)} danger disabled={!checked || isDeleting || !selectedReason}>
						Yes
					</Button>,
				]}>
				<p>
					Do you wish to continue to close this job?{' '}
					<Alert className="mt-1 font-medium" severity="error">
						This action cannot be undone. Once closed, the job will no longer be visible to applicants and you will not be able to re-open it.
					</Alert>
				</p>
				<Form form={form} className="mt-4" layout="vertical">
					<Form.Item name="completed_reason" label="Reason for closing">
						<Select
							onChange={(value) => {
								form.setFieldsValue({completed_other_reason: ''});
								setSelectedReason(value); // Update state to trigger re-render
							}}>
							<Select.Option value="position_filled_through_permworks">Position filled through Permworks</Select.Option>
							<Select.Option value="position_filled_through_other_platform">Position filled through other platform</Select.Option>
							<Select.Option value="budget_constraints">Budget constraints</Select.Option>
							<Select.Option value="role_cancelled">Role cancelled</Select.Option>
							<Select.Option value="insufficient_candidates">Insufficient candidates</Select.Option>
							<Select.Option value="hiring_paused">Hiring paused</Select.Option>
							<Select.Option value="other">Other</Select.Option>
						</Select>
					</Form.Item>
					{selectedReason === 'other' && (
						<Form.Item name="completed_other_reason" label="Other reason">
							<Input placeholder="Please specify the reason" />
						</Form.Item>
					)}
				</Form>
				<Checkbox type="checkbox" className="flex items-top gap-1 mt-2" checked={checked} onChange={() => setChecked(!checked)}>
					I understand that this action cannot be undone.
				</Checkbox>
			</Modal>
			{authenticatedButtons()}
		</>
	);
}
